import React, { FC, useState } from "react";
import { useNavigate } from "react-router";
import {
    ProgramItem,
    ProgramBox,
    ProgramContent,
    ProgramFlex,
    ProgramStack,
    ProgramTitle,
    ProgramText,
    ProgramImage,
    useProgram,
} from "planby";

import dayjs from "dayjs";
import { useEpgEventDetailOverrideById } from "../../../../services/epg/Query";
import { EpgEventDetail, EpgEventDetailOverride, EpgEventInstance } from "../../../../models/epg/Models";

type Props = ProgramItem;

const Program: FC<Props> = ({
    program,
    ...rest
}: Props) => {

    const navigate = useNavigate();

    const [ isImageError, setIsImageError ] = useState(false);

    const { styles, formatTime, set12HoursTimeFormat, isLive, isMinWidth } =
        useProgram({
            program,
            ...rest,
        });

    const { data } = program;
    const { id, image, title, since, till } = data;

    const sinceTime = formatTime(since, set12HoursTimeFormat()).toLowerCase();
    const tillTime = formatTime(till, set12HoursTimeFormat()).toLowerCase();

    const { data: epgEvent } = useEpgEventDetailOverrideById(id);

    const isCurrentlyPlaying = (instance: EpgEventInstance) => {
        return dayjs().isBetween(dayjs(instance.startDateTime), dayjs(instance.endDateTime));
    };

    const indicateEmptyFields = () => {
        if (!epgEvent)
            return;

        const detailOverride = epgEvent.detailOverride;
        const instance = epgEvent.instance;
        const detail = epgEvent.detail;

        const isParentRatingEmpty = (!detailOverride?.parentalRatings?.length) && (!detail.parentalRatings?.length);

        if (isParentRatingEmpty) {
            return { "background": isCurrentlyPlaying(instance) ? "#eb2222" : "#a94848" };
        }

        const isCategoriesEmpty = (!detailOverride?.categories?.length) && (!detail?.categories?.length);

        if (isCategoriesEmpty) {
            return { "background": isCurrentlyPlaying(instance) ? "#eb2222" : "#a94848" };
        }

        if (detailOverride){
            const isEmpty = Object.keys(detailOverride).some((key) => {
                if (key === "title" || key === "subTitle" || key === "categories" || key === "keywords")
                    return false;

                const detailOverrideValue = detailOverride[key as keyof EpgEventDetailOverride];
                const detailValue = detail[key as keyof EpgEventDetail];

                if (!detailOverrideValue && !detailValue)
                    return true;

                const isEmptyArray = (Array.isArray(detailOverrideValue) && detailOverrideValue.length === 0) &&
                    (Array.isArray(detailValue) && detailValue.length === 0);
                if (isEmptyArray)
                    return true;

                return detailOverrideValue && detailOverrideValue.length === 0 && detailValue && detailValue.length === 0;
            });

            if (isEmpty)
                return { "background": isCurrentlyPlaying(instance) ? "#a99648" : "#bf9b0a" };
        }

        return;
    };

    return (
        <ProgramBox width={styles.width} style={styles.position} onClick={() => {
            navigate(`${id}`);
        }}
        >
            <ProgramContent width={styles.width} isLive={isLive} style={indicateEmptyFields()}>
                <ProgramFlex>
                    {
                        isLive && isMinWidth &&
                        <ProgramImage
                            src={!isImageError ? image : "/dist/images/placeholder-image.jpg" }
                            alt="Preview"
                            onError={() => { if (!isImageError) setIsImageError(true); }}
                        />
                    }
                    <ProgramStack>
                        <ProgramTitle>
                            {title}
                        </ProgramTitle>
                        <ProgramText className= {`mr-1 float-left ${indicateEmptyFields() ? "text-white" : ""}`}>
                            {`${sinceTime} - ${tillTime}`}
                        </ProgramText>
                    </ProgramStack>
                </ProgramFlex>
            </ProgramContent>
        </ProgramBox>
    );
};

export default Program;
